import { useEffect } from 'react';
import { useQuery, ApolloError } from '@apollo/client';
import { log } from 'utils/log';
import get from 'lodash/get';
import { ApiFilterMetaObj } from 'types';
import { ControllerQueryConfig, UpdateResultGeneric } from '../../types';

const useGenericSearchQuery = <I>({
    queryConfig,
    queryString,
    page,
    itemsPerPage,
    updateResult,
    updateLoading,
    updateError,
    updateCompleted,
}: {
    queryConfig: ControllerQueryConfig;
    queryString: string;
    page: number;
    itemsPerPage: number;
    updateResult: UpdateResultGeneric<I>;
    updateLoading: (loading: boolean) => void;
    updateError: (error: ApolloError) => void;
    updateCompleted: () => void;
}) => {
    const { loading } = useQuery(queryConfig?.query, {
        skip: !!queryConfig?.skip,
        variables: {
            take: itemsPerPage,
            page: page,
            query: queryString,
            offset: 0,
            ...queryConfig?.variables,
        },
        onError: (error) => {
            log.error(
                queryConfig?.failureEvent.message ?? '',
                queryConfig?.failureEvent.eventId,
                error,
            );

            updateError(error);
        },
        onCompleted: (data) => {
            updateCompleted();
            if (data) {
                const pathToItems = `${queryConfig.pathToItems}.data`;
                const pathToTotalItems = `${queryConfig.pathToItems}.meta[${queryConfig.pathToTotalItems}]`;
                const pathToFilters = `${queryConfig.pathToItems}.meta.filters`;

                const items = get<I[]>(data, pathToItems, []);
                const total = get<number | undefined>(
                    data,
                    pathToTotalItems,
                    undefined,
                );
                const filterMetaObj = get<ApiFilterMetaObj | undefined>(
                    data,
                    pathToFilters,
                    undefined,
                );

                updateResult({
                    items,
                    total,
                    filterMetaObj,
                });
            }
        },
    });

    useEffect(() => {
        updateLoading(loading);
    }, [loading, updateLoading]);
};

export default useGenericSearchQuery;
